/*
 * Scroll "scenes": a section marks a block with [data-scene] and styles its
 * entrance in its own CSS, keyed off two attributes this script sets:
 *
 *   [data-armed]   the block will play; it may hold its "before" pose
 *   [data-played]  it has scrolled into view; its entrance runs
 *
 * Numbers inside a playing block with data-count-to count up to that value
 * (data-decimals for places after the point), like a timecode running.
 *
 * With reduced motion, or without IntersectionObserver, nothing is armed,
 * so every block simply shows as it is. Blocks already scrolled past when
 * the page loads, or by a back/forward visit's restored scroll position,
 * play at once, so they are never left in their "before" pose above the fold.
 *
 * A block without a box yet waits for one before it is judged: touch
 * devices skip rendering the lower sections (content-visibility: auto, see
 * theme.js) until an idle moment or the first touch, and the observer
 * reports a block in a skipped section as outside with an empty rect - which
 * must not read as "scrolled past", or the block would play unseen at load
 * and sit settled by the time it is scrolled to. The same goes for a block
 * that is display: none for now (a phone-only or desktop-only variant).
 * Once it has a box it is observed afresh and plays when it comes into view.
 * So a [data-scene] needs a box of its own: not display: contents.
 */
(() => {
  if (window.motionScenes) {
    window.motionScenes.arm(document);
    return;
  }

  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supported = 'IntersectionObserver' in window;

  const countUp = (element) => {
    const target = parseFloat(element.dataset.countTo);
    if (Number.isNaN(target)) return;
    const decimals = parseInt(element.dataset.decimals || '0', 10);
    const duration = parseInt(element.dataset.countDuration || '1100', 10);
    // Use one clock: DevTools' slowed animation frames can have a
    // different time origin from performance.now().
    let start = null;
    const frame = (now) => {
      if (start === null) start = now;
      const t = Math.max(0, Math.min((now - start) / Math.max(1, duration), 1));
      const eased = 1 - Math.pow(1 - t, 3);
      const text = (target * eased).toFixed(decimals);
      if (element.textContent !== text) element.textContent = text;
      if (t < 1) requestAnimationFrame(frame);
    };
    element.textContent = (0).toFixed(decimals);
    requestAnimationFrame(frame);
  };

  const play = (scene) => {
    if (scene.hasAttribute('data-played')) return;
    scene.setAttribute('data-played', '');
    scene.querySelectorAll('[data-count-to]').forEach(countUp);
    if (scene.matches('[data-count-to]')) countUp(scene);
  };

  // blocks waiting for a box; observing one afresh gets a new first report
  const boxed = supported && 'ResizeObserver' in window ? new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      const size = entry.borderBoxSize?.[0];
      if (!(size ? size.inlineSize || size.blockSize : entry.contentRect.width || entry.contentRect.height)) return;
      boxed.unobserve(entry.target);
      observer.unobserve(entry.target);
      observer.observe(entry.target);
    });
  }) : null;

  // A block plays once 15% of it is in view, or once it fills 15% of the
  // viewport: a block taller than about 6.5 screens (a long legal page) can
  // never show 15% of itself at once, and would otherwise stay hidden. The
  // finer steps make the observer report such a block as it scrolls in.
  const enoughInView = (entry) => {
    const viewport = entry.rootBounds?.height || window.innerHeight;
    const need = Math.min(entry.boundingClientRect.height, viewport) * 0.15;
    return entry.intersectionRect.height >= need;
  };

  const observer = supported ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const scene = entry.target;
      if (entry.isIntersecting && !enoughInView(entry)) return;
      if (!entry.isIntersecting) {
        const box = entry.boundingClientRect;
        // no box yet (a skipped section, display: none): judged once it has one
        if (boxed && !box.width && !box.height && !box.top && !box.left) {
          boxed.observe(scene);
          return;
        }
        // still below the fold
        if (box.top > 0) return;
      }
      // in view, or already scrolled past (a restored scroll position)
      observer.unobserve(scene);
      boxed?.unobserve(scene);
      play(scene);
    });
  }, { threshold: [0, 0.005, 0.01, 0.02, 0.04, 0.07, 0.1, 0.15], rootMargin: '0px 0px -6% 0px' }) : null;

  const arm = (root) => {
    if (!observer || still.matches) return;
    root.querySelectorAll('[data-scene]:not([data-armed])').forEach((scene) => {
      scene.setAttribute('data-armed', '');
      observer.observe(scene);
    });
  };

  // turning reduced motion on mid-visit shows everything as it is
  still.addEventListener?.('change', () => {
    if (!still.matches) return;
    document.querySelectorAll('[data-scene][data-armed]').forEach((scene) => {
      observer?.unobserve(scene);
      boxed?.unobserve(scene);
      scene.setAttribute('data-played', '');
    });
  });

  // back/forward: the browser restores the scroll position once the page is
  // tall enough, often after the blocks were first judged, and a jump that
  // carries a block from below the fold to above it crosses nothing the
  // observer reports. Until the visitor's first input, each such scroll has
  // the waiting blocks judged afresh, so none is left in its "before" pose.
  if (observer && performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward') {
    const inputs = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
    let frame = 0;
    const rejudge = () => {
      frame = 0;
      document.querySelectorAll('[data-scene][data-armed]:not([data-played])').forEach((scene) => {
        observer.unobserve(scene);
        observer.observe(scene);
      });
    };
    const restored = () => {
      if (!frame) frame = requestAnimationFrame(rejudge);
    };
    const stop = () => {
      removeEventListener('scroll', restored);
      inputs.forEach((type) => removeEventListener(type, stop, true));
    };
    addEventListener('scroll', restored, { passive: true });
    inputs.forEach((type) => addEventListener(type, stop, { capture: true, passive: true }));
  }

  // Release a finished monitor's reveal mask so its external hover shadow
  // isn't clipped. A replay reinstates the mask before its next frame.
  const monitorWipe = (event) => {
    if (!event.animationName.startsWith('upp-story-wipe-') ||
        !event.target.matches('[class*="upp-story__monitor-"]')) return;
    event.target.toggleAttribute('data-wipe-complete', event.type === 'animationend');
  };
  document.addEventListener('animationstart', monitorWipe);
  document.addEventListener('animationend', monitorWipe);

  window.motionScenes = { arm, play };
  arm(document);
  document.addEventListener('shopify:section:load', (event) => arm(event.target));
})();

